import { useEffect, useMemo, type ReactNode } from 'react';
import Popup from 'devextreme-react/popup';
import type { ToolbarItem } from 'devextreme/ui/popup';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { useMediaPreviewPopupSize } from './useMediaPreviewPopupSize';
import './MediaPreviewPopup.css';

export interface FilePreviewPopupShellProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Shared DevExtreme Popup chrome for file-browser previews (media, PDF, documents).
 * Use the built-in title string so Restore / Maximize / Close stay on the title bar.
 */
export function FilePreviewPopupShell({
  visible,
  title,
  onClose,
  children
}: FilePreviewPopupShellProps) {
  const { size, isMaximized, maximize, restoreDefault, onResizeEnd, maxWidth, maxHeight } =
    useMediaPreviewPopupSize();
  const responsive = useResponsivePopupOptions({
    width: size.w,
    height: size.h
  });

  const locked = Boolean(responsive.fullScreen) || isMaximized;

  const toolbarItems = useMemo((): ToolbarItem[] => {
    if (responsive.fullScreen) return [];

    return [
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'revert',
          hint: 'Restore default size',
          stylingMode: 'text',
          elementAttr: { class: 'file-browser-preview-popup__title-btn' },
          onClick: () => restoreDefault()
        }
      },
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'fullscreen',
          hint: isMaximized ? 'Already maximized' : 'Maximize',
          stylingMode: 'text',
          disabled: isMaximized,
          elementAttr: { class: 'file-browser-preview-popup__title-btn' },
          onClick: () => maximize()
        }
      }
    ];
  }, [responsive.fullScreen, isMaximized, maximize, restoreDefault]);

  useEffect(() => {
    if (!visible || !title) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelectorAll('.file-browser-preview-popup-wrapper .dx-popup-title .dx-toolbar-label')
        .forEach((el) => {
          if (el instanceof HTMLElement) el.title = title;
        });
    });
    return () => cancelAnimationFrame(frame);
  }, [visible, title]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      title={title}
      showCloseButton
      toolbarItems={toolbarItems}
      width={responsive.width ?? size.w}
      height={responsive.height ?? size.h}
      fullScreen={responsive.fullScreen}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      resizeEnabled={!locked}
      dragEnabled={!locked}
      onResizeEnd={onResizeEnd}
      wrapperAttr={{ class: 'file-browser-preview-popup-wrapper' }}
    >
      {children}
    </Popup>
  );
}
